import {
  GridSortModel,
  DataGridProProps,
  GridInitialState,
} from "@mui/x-data-grid-pro";
import { DataTableColDef } from "../../components/common/DataTable/DataTable";
import FeatureFlag from "../../types/feature-flags/featureFlag";
import { useEditFeatureFlag } from "../../hooks/useFeatureFlag";
import { useAlert } from "../../context/AlertContext";
import { useState } from "react";
import ResultsPage from "../../components/common/ResultsPage/ResultsPage";
import FeatureFlagDialog from "./components/FeatureFlagDialog";
import { FeatureFlagFormValues } from "../../api/featureFlag";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

export const FEATURE_FLAGS_DEFAULT_SORT_MODEL: GridSortModel = [
  {
    field: "customerId",
    sort: "desc",
  },
];

export interface FeatureFlagWithName extends FeatureFlag {
  customerName: string;
}

interface FeatureFlagResultsProps
  extends Omit<
    DataGridProProps,
    "columns" | "rows" | "getRowId" | "initialStateValue"
  > {
  tableId: string;
  results: FeatureFlagWithName[];
  isLoading: boolean;
  refresh: () => void | Promise<void>;
  onSortModelChange?: (sortModel: GridSortModel) => Promise<void> | void;
  setFeatures: React.Dispatch<React.SetStateAction<FeatureFlag[]>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pageIndex?: number;
}

export default function FeatureFlagResults({
  tableId,
  results,
  isLoading,
  refresh,
  onSortModelChange,
  pageIndex,
  setFeatures,
  isOpen,
  setIsOpen,
  ...props
}: Readonly<FeatureFlagResultsProps>) {
  const { t } = useTranslation("grid");
  const initialState: GridInitialState = {
    ...props.initialState,
    columns: {},
    sorting: {
      sortModel: FEATURE_FLAGS_DEFAULT_SORT_MODEL,
      ...props.initialState?.sorting,
    },
  };
  const getRowId = (row: FeatureFlag) => `${row.customerId}-${row.featureName}`;
  const editFeatureFlag = useEditFeatureFlag();
  const showAlert = useAlert();
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<FeatureFlag | null>(null);
  const [formValues, setFormValues] = useState<
    FeatureFlagFormValues | undefined
  >(undefined);

  const editSite = async (
    customerId: string,
    featureName: string,
    data: FeatureFlagFormValues
  ) => {
    try {
      const { featureFlag, errors } = await editFeatureFlag.mutateAsync({
        customerId,
        featureName,
        ...data,
      });
      if (!featureFlag) {
        const errorMap = (errors as any)?.errors as Map<string, string[]>;
        const generalErrorMessages =
          errorMap?.get("generalErrors")?.join(",") ||
          "Failed to updated feature flag";
        showAlert(generalErrorMessages, "error");
        return;
      }

      setFeatures((prevFeatures: FeatureFlag[]) => {
        return [
          ...prevFeatures.filter(
            (f) => f.customerId !== customerId && f.featureName !== featureName
          ),
          featureFlag,
        ];
      });
      showAlert("Feature flag updated successfully", "success");
    } catch (err: unknown) {
      console.error("Failed to update feature flag:", err);
      showAlert(`Unable to update Feature:${err}`, "error");
    }
  };

  const columns: DataTableColDef[] = [
    {
      field: "customerName",
      headerName: t("grid:customer"),
      minWidth: 250,
    },

    {
      field: "customerId",
      headerName: t("grid:customerId"),
      minWidth: 300,
    },
    {
      field: "featureName",
      headerName: t("grid:feature"),
      minWidth: 200,
    },
    {
      field: "isEnabled",
      headerName: t("grid:enabled"),
      showMenuOnHover: false,
      renderCell: ({ row }) => (
        <span>{row.isEnabled ? "Enabled" : "Disabled"}</span>
      ),
    },
    {
      field: "expiration",
      headerName: t("grid:expirationDate"),
      minWidth: 150,
      renderCell: ({ value }) =>
        value ? dayjs(value * 1000).format("MM/DD/YYYY, hh:mm:ss A") : "",
    },
  ];

  const handleSortModelChange = (model: GridSortModel) => {
    const newModel =
      model.length === 0 ? FEATURE_FLAGS_DEFAULT_SORT_MODEL : model;
    onSortModelChange?.(newModel);
    return newModel;
  };

  return (
    <div className="h-full flex flex-col">
      <ResultsPage
        disableCreatable={true}
        disableDeletable={true}
        permissionResource="customers"
        entityName="Feature Flag"
        tableId="feature-flags"
        columns={columns}
        rows={results}
        loading={isLoading}
        initialState={initialState}
        sortingMode="server"
        filterMode="server"
        getRowId={getRowId}
        selectedRowId={selectedRowId}
        setSelectedRowId={setSelectedRowId}
        pageIndex={pageIndex}
        onSortModelChange={handleSortModelChange}
        setEditFormOpen={(open: boolean) => {
          const selected = results.find((r) => getRowId(r) === selectedRowId);
          if (selected) {
            setFormValues({
              isEnabled: selected.isEnabled,
              expiration: selected.expiration
                ? new Date(selected.expiration * 1000).toISOString()
                : null,
            });
            setSelectedRow(selected);
          }
          setIsOpen(open);
        }}
      />
      <FeatureFlagDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        formValues={formValues}
        selectedRow={selectedRow!!}
        editSite={editSite}
      />
    </div>
  );
}
